import React, { useEffect, useState } from 'react';
import Logo from './Logo';
import { Facebook, Twitter, Send, MapPin, Mail } from 'lucide-react';
import API from '../lib/api';
import { useAuth } from '../context/AuthContext';

const socialsList = [
  { key: 'facebook', Icon: Facebook },
  { key: 'twitter', Icon: Twitter },
  { key: 'telegram', Icon: Send },
];

const pillarsLegal = ['About the Community', 'Login / Register', 'Investment Desk', 'Ecosystem News'];
const bottomLinks = ['Terms of Use', 'Privacy Policy', 'Refund Policy', 'Risk Warning'];

const Footer = ({ onOpenPortal, onNavigate }) => {
  const [socials, setSocials] = useState({});
  const [contactEmail, setContactEmail] = useState('');
  const { user } = useAuth();
  useEffect(() => {
    (async () => {
      try {
        const { data } = await API.get('/settings');
        setSocials(data.socials || {});
        setContactEmail(data.contact_email || '');
      } catch {}
    })();
  }, []);

  const handleLink = (label) => {
    if (label === 'Login / Register') return onOpenPortal?.();
    if (label === 'About the Community') {
      // If we are on any dashboard view, first bring the user back to Home
      // (so the pillars section actually exists in the DOM), then smoothly
      // scroll it into view.
      onNavigate?.('home');
      requestAnimationFrame(() => {
        setTimeout(() => {
          const el = document.getElementById('pillars');
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          else window.scrollTo({ top: 0, behavior: 'smooth' });
        }, 60);
      });
      return;
    }
    // Guest visitors: prompt sign-in before hitting authed views (matches header behaviour)
    if (!user) return onOpenPortal?.();
    if (label === 'Ecosystem News') return onNavigate?.('news');
    if (label === 'Investment Desk') return onNavigate?.('investments', 'packages');
  };

  return (
    <footer className="px-4 md:px-10 pt-16 pb-6 border-t border-emerald-900/60 bg-[#03110a]">
      <div className="mx-auto max-w-[1240px]">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
          <div>
            <Logo />

            <div className="mt-5 flex items-start gap-2 text-emerald-100/70 text-sm max-w-xs" data-testid="company-address">
              <MapPin size={14} className="mt-0.5 text-emerald-400 shrink-0" />
              <address className="not-italic leading-relaxed">
                <div className="text-white font-medium">SkywayGreen.LLC</div>
                Katray Street 11 Building,<br />
                Katray, Gross Cent,<br />
                Saint Lucia
              </address>
            </div>

            {contactEmail && (
              <a
                href={`mailto:${contactEmail}`}
                data-testid="company-email"
                className="mt-3 inline-flex items-center gap-2 text-emerald-100/80 hover:text-emerald-300 text-sm transition"
              >
                <Mail size={14} className="text-emerald-400" />
                {contactEmail}
              </a>
            )}
          </div>

          <div>
            <div className="text-white text-sm font-semibold">Pillars &amp; Legal</div>
            <ul className="mt-4 space-y-2.5">
              {pillarsLegal.map((l) => (
                <li key={l}>
                  <button
                    onClick={() => handleLink(l)}
                    data-testid={`footer-link-${l.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                    className="text-emerald-100/60 text-sm hover:text-emerald-300 transition text-left"
                  >
                    {l}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <div className="text-white text-sm font-semibold">Social Media Channels</div>
            <div className="mt-4 grid grid-cols-3 gap-3">
              {socialsList.filter((s) => socials[s.key]).map(({ key, Icon }) => (
                <a key={key} href={socials[key]} target="_blank" rel="noreferrer noopener" className="h-10 w-full rounded-xl border border-emerald-800/60 bg-emerald-950/40 flex items-center justify-center text-emerald-300 hover:bg-emerald-900/50 transition">
                  <Icon size={16} />
                </a>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-14 pt-6 border-t border-emerald-900/50 flex flex-col md:flex-row justify-between gap-3 text-xs text-emerald-100/50">
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
            {bottomLinks.map((l) => (<a key={l} href="#" className="hover:text-emerald-300 transition">{l}</a>))}
          </div>
          <div>Copyright &copy; {new Date().getFullYear()} SkyWay Green. All rights reserved.</div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
